import { type ComponentType, lazy } from "react";

/** Remembered for the tab's session: a reload that did not help is not repeated. */
const RELOADED = "fm-reloaded-for-new-build";

/** How long a page's file may take (the owner, 2026-10-12; D-84). A download
 * the network stalls neither arrives nor fails, and on 2026-10-11 one left
 * REGISTRATION on "Loading…" for good; this turns the wait into a failure,
 * which the reload below answers. */
export const PATIENCE_MS = 15_000;

/** A page fetched the first time somebody opens it, not with the app.
 *
 * Every screen used to arrive in one file of about a megabyte, so a delegate
 * opening a registration link or the room's Q&A on a phone downloaded the whole
 * admin first (docs/PERFORMANCE.md § 13). Now the first visit carries the frame
 * and the one page asked for.
 *
 * The price is a tab opened before a deploy: it asks for a page file the new
 * build does not have, and the server answers 404. One reload fetches the new
 * build and the page with it, as it opens a fresh connection for a download
 * that stalled. A second failure in the same tab is a real one, and it is
 * thrown to the error boundary rather than reloaded forever. */
export function lazyPage<K extends string, M extends Record<K, ComponentType>>(
  load: () => Promise<M>,
  name: K,
) {
  return lazy(async () => {
    try {
      const module = await withinPatience(load);
      remember(false);
      return { default: module[name] };
    } catch (error) {
      if (remember(true)) throw error;
      window.location.reload();
      // Never settles: the page is going away, and rendering the error
      // boundary for the moment before it does is a flash of "Something broke".
      return new Promise<never>(() => {});
    }
  });
}

/** `load`, or a failure once `PATIENCE_MS` has passed without it. */
function withinPatience<T>(load: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("This page took too long to arrive. Check the connection.")),
      PATIENCE_MS,
    );
    load().then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Whether this tab has already reloaded for a missing page, then records `now`.
 * Storage can be refused (a private window); then nothing is remembered and a
 * missing page is thrown, never reloaded, which cannot loop. */
function remember(now: boolean): boolean {
  try {
    const before = sessionStorage.getItem(RELOADED) === "1";
    if (now) sessionStorage.setItem(RELOADED, "1");
    else sessionStorage.removeItem(RELOADED);
    return before;
  } catch {
    return true;
  }
}
